import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, Col, Container, Row } from "react-bootstrap";
import {
  Header,
  SidePanelWrapper,
  SidePanelPlatformsWrapper,
  SidePanelSubscriptions,
  FooterWrapper,
  Loader,
  TabItem,
  Switcher,
} from "../../components";
import { useGetPlatforms } from "../../queries/useGetApps";
import { useGetMySubscriptions } from "../../queries/useGetMySubscriptions";
import { useGetCustomers } from "../../queries/useGetCustomers";
import { useGetContracts } from "../../queries/useGetContracts";
import { useGetContractView } from "../../queries/useGetContractView";
import { useGetContractDownload } from "../../queries/useGetContractDownload";
import { useOktaAuth } from "@okta/okta-react";
import { getFilteredServices, handleLogout, validateAuthentication } from "../../utils/helpers";
import "../../styles/screens/contracts.scss";
import viewEyeIcon from "../../assets/icons/viewEyeIcon.svg";
import downloadIcon from "../../assets/icons/downloadIcon.svg";

const ViewButton = ({ s3Key }: { s3Key: string }) => {
  const { oktaAuth } = useOktaAuth();
  const { refetch, isFetching } = useGetContractView(s3Key);

  const handleClick = async () => {
    try {
      const isAuthenticated = await validateAuthentication();
      if (!isAuthenticated) {
        await handleLogout(oktaAuth);
        return;
      }
    } catch {
      console.error("error occurred while authenticating");
    }
    const result = await refetch();
    if (result.data) {
      window.open(result.data, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <button className="contracts-view-btn" onClick={handleClick} disabled={isFetching}>
      <span className="btn-text">{isFetching ? "Getting link…" : "View"}</span>
      <img src={viewEyeIcon} className="btn-icon" alt="View" />
    </button>
  );
};

const DownloadButton = ({ s3Key }: { s3Key: string }) => {
  const { oktaAuth } = useOktaAuth();
  const { refetch, isFetching } = useGetContractDownload(s3Key);

  const handleClick = async () => {
    try {
      const isAuthenticated = await validateAuthentication();
      if (!isAuthenticated) {
        await handleLogout(oktaAuth);
        return;
      }
    } catch {
      console.error("error occurred while authenticating");
    }
    const result = await refetch();
    if (result.data) {
      const anchor = document.createElement("a");
      anchor.href = result.data;
      anchor.download = "";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  return (
    <button className="contracts-download-btn" onClick={handleClick} disabled={isFetching}>
      <span className="btn-text">{isFetching ? "Downloading…" : "Download"}</span>
      <img src={downloadIcon} className="btn-icon" alt="Download" />
    </button>
  );
};

const ContractAccordionBody = ({ customerName }: { customerName: string }) => {
  const { data: contracts, isLoading, isError } = useGetContracts(customerName);

  if (isLoading) return <Loader />;
  if (isError) return <p className="contracts-empty">Failed to load documents.</p>;
  if (!contracts || contracts.length === 0) {
    return <p className="contracts-empty">No documents found for {customerName}.</p>;
  }

  return (
    <div className="contracts-accordion-body">
      {contracts.map((contract) => (
        <div key={contract.key} className="contracts-item-row">
          <span className="contracts-item-name">{contract.name.replace(/\.[^/.]+$/, "")}</span>
          <div className="contracts-item-actions">
            <ViewButton s3Key={contract.key} />
            <DownloadButton s3Key={contract.key} />
          </div>
        </div>
      ))}
    </div>
  );
};

export const Contracts = () => {
  const navigate = useNavigate();
  const { authState } = useOktaAuth();
  const { data: platforms } = useGetPlatforms();
  const { data: mySubscriptions } = useGetMySubscriptions();
  const isContractsSubscribed = mySubscriptions?.some((s) => s.appName === "contracts") ?? false;
  const { data: customers, isLoading: customersLoading, isError: customersError } = useGetCustomers(isContractsSubscribed);

  const contractsPlatform = platforms?.find((p) => p.appName === "contracts");
  const contractsImages = contractsPlatform?.template?.images ?? [];
  const nonSubscribedServices = getFilteredServices(platforms ?? [], mySubscriptions!);

  useEffect(() => {
    if (platforms && !contractsPlatform) {
      navigate("/");
    }
  }, [platforms, contractsPlatform, navigate]);

  useEffect(() => {
    if (contractsPlatform?.image) {
      const backgroundElement = document.querySelector(".template-services-background-img") as HTMLElement;
      if (backgroundElement) {
        backgroundElement.style.backgroundImage = `linear-gradient(rgba(16, 24, 32, 0.4), rgba(16, 24, 32, 0.4)), url(${contractsPlatform.image})`;
      }
    }
  }, [contractsPlatform?.image]);

  const [showSidePanel, setShowSidePanel] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [activeKey, setActiveKey] = useState<string | null>("0");

  const handleClickOpenSidePanel = () => setShowSidePanel(!showSidePanel);

  const tabSwitcherData: TabItem[] = [
    {
      title: "Your subscriptions",
      content: <SidePanelSubscriptions />,
      hide: !authState?.accessToken?.accessToken,
    },
    {
      title: "All Services",
      content: <SidePanelPlatformsWrapper onClose={() => setShowSidePanel(false)} data={nonSubscribedServices} />,
      hide: nonSubscribedServices.length === 0,
    },
  ];

  return (
    <>
      <Container fluid className="header-container dark">
        <Header handleClickOpenSidePanel={handleClickOpenSidePanel} />
      </Container>

      <Row className="template-services-background-img">
        <Col className="template-services-header-section">
          {contractsPlatform?.name && (
            <>
              <div className="template-services-header">
                <div className="template-services-graphic-line" />
                <h1 style={{ marginBottom: "-7px" }}>{contractsPlatform.name.split(" ")[0]}</h1>
              </div>
              <h1>{contractsPlatform.name.split(" ").slice(1).join(" ")}</h1>
            </>
          )}
          <p className="subheading">{contractsPlatform?.template?.title}</p>
        </Col>
      </Row>

      <Row className="template services-wrapper">
        <Col>
          <Row className="flex-column">
            <Col className="description">
              {contractsPlatform?.template?.description
                ?.split("\n")
                .map((line, index) => <p key={index}>{line}</p>)}
            </Col>
          </Row>

          <div className="graphic-line-service-wrapper service-page" />

          <Row>
            <Col className="list">
              {customersLoading && <Loader />}

              {customersError && (
                <p className="contracts-empty">Failed to load customer list. Please try again.</p>
              )}

              {!customersLoading && !customersError && (!isContractsSubscribed || customers?.length === 0) && (
                <p className="contracts-empty">You have no documents assigned to your account.</p>
              )}

              {customers && customers.length > 0 && (
                <Accordion
                  className="accordion-list"
                  flush
                  activeKey={activeKey ?? undefined}
                  onSelect={(k) => setActiveKey(k as string | null)}
                >
                  {customers.map((customer, index) => (
                    <Accordion.Item key={customer.CustomerID} eventKey={index.toString()}>
                      <Accordion.Header>{customer.Name}</Accordion.Header>
                      <Accordion.Body>
                        <ContractAccordionBody customerName={customer.Name} />
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Col>
          </Row>
        </Col>

        <Col xs={1} />

        <Col className="contracts-right-image-col" style={{ position: "relative" }}>
          {Array.isArray(contractsImages) && contractsImages.length > 0 && (
            <Switcher useFixed={true} images={contractsImages} />
          )}
        </Col>
      </Row>

      {showSidePanel && (
        <SidePanelWrapper
          showSidePanel={showSidePanel}
          setShowSidePanel={setShowSidePanel}
          tabSwitcherData={tabSwitcherData}
          selectedTab={!authState?.accessToken?.accessToken ? selectedTab + 1 : selectedTab}
          setSelectedTab={setSelectedTab}
        />
      )}

      <FooterWrapper isTemplate={true} />
    </>
  );
};
